import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import ClientCombobox from "@/components/ClientCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { format, differenceInDays, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
  CalendarIcon,
  User,
  MapPin,
  Users,
  Plane,
  Hotel,
  Car,
  Compass,
  UtensilsCrossed,
  Building2,
  Plus,
  Trash2,
  Save,
  Eye,
  ArrowLeft,
  Percent,
  FileText,
  Search,
  UserPlus,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Bold,
  Italic,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Palette
} from "lucide-react";
import QuotePreviewPanel from "@/components/QuotePreviewPanel";
import UnsavedChangesGuard from "@/components/UnsavedChangesGuard";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

// Editor Toolbar Component
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const colors = ['#000000', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#2563eb', '#7c3aed', '#db2777'];

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-slate-50 rounded-t-md">
      <Button
        type="button"
        variant={editor.isActive('bold') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className="h-8 w-8 p-0"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('italic') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className="h-8 w-8 p-0"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="h-8 mx-1" />
      <Button
        type="button"
        variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className="h-8 w-8 p-0"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className="h-8 w-8 p-0"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="h-8 mx-1" />
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className="h-8 w-8 p-0"
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className="h-8 w-8 p-0"
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className="h-8 w-8 p-0"
      >
        <AlignRight className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="h-8 mx-1" />
      <div className="flex items-center gap-1">
        <Palette className="h-4 w-4 text-slate-500" />
        {colors.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => editor.chain().focus().setColor(color).run()}
            className={`w-5 h-5 rounded border border-slate-300 hover:scale-110 transition-transform ${
              editor.isActive('textStyle', { color }) ? 'ring-2 ring-offset-1 ring-blue-500' : ''
            }`}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    </div>
  );
};

const serviceTypes = [
  { value: "flight", label: "Vuelo", icon: Plane },
  { value: "hotel", label: "Hotel", icon: Hotel },
  { value: "transfer", label: "Traslado", icon: Car },
  { value: "assistance", label: "Asistencia al Viajero", icon: Users },
  { value: "tour", label: "Tour / Actividades", icon: Compass },
  { value: "meal", label: "Alimentación", icon: UtensilsCrossed },
  { value: "conference", label: "Sala de Conferencias", icon: Building2 },
  { value: "other", label: "Otro", icon: Plus },
];

const roomTypes = [
  { value: "sencilla", label: "Sencilla" },
  { value: "doble", label: "Doble" },
  { value: "triple", label: "Triple" },
  { value: "cuadruple", label: "Cuádruple" },
  { value: "suite", label: "Suite" },
  { value: "suite_junior", label: "Suite Junior" },
  { value: "suite_presidencial", label: "Suite Presidencial" },
  { value: "villa", label: "Villa" },
  { value: "bungalow", label: "Bungalow" },
  { value: "familiar", label: "Familiar" },
  { value: "deluxe", label: "Deluxe" },
  { value: "superior", label: "Superior" },
  { value: "estandar", label: "Estándar" },
];

const flightTypes = [
  { value: "roundtrip", label: "Viaje Redondo" },
  { value: "oneway", label: "Solo Ida" },
];

const ivaOptions = [
  { value: 0, label: "0%" },
  { value: 16, label: "16%" },
];

const coverageTypes = [
  { value: "basica", label: "Básica" },
  { value: "estandar", label: "Estándar" },
  { value: "premium", label: "Premium" },
  { value: "todo_riesgo", label: "Todo Riesgo" },
  { value: "medica", label: "Médica" },
  { value: "cancelacion", label: "Cancelación de Viaje" },
];

const NewQuote = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  // Client search state
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientSearchRef = useRef(null);

  // TipTap editor for terms
  const termsEditor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, terms: editor.getHTML() }));
      setHasUnsavedChanges(true);
    }
  });

  // Airport search state for flight services
  const [airportSearches, setAirportSearches] = useState({});
  const [airportResults, setAirportResults] = useState({});
  const [showAirportDropdown, setShowAirportDropdown] = useState({});

  // Search airports
  const searchAirports = async (query, serviceId, field) => {
    if (query.length < 2) {
      setAirportResults(prev => ({ ...prev, [`${serviceId}_${field}`]: [] }));
      return;
    }
    try {
      const res = await axios.get(`${API}/airports/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAirportResults(prev => ({ ...prev, [`${serviceId}_${field}`]: res.data }));
    } catch (error) {
      console.error("Error searching airports:", error);
    }
  };

  const handleAirportSearchChange = (serviceId, field, value) => {
    setAirportSearches(prev => ({ ...prev, [`${serviceId}_${field}`]: value }));
    setShowAirportDropdown(prev => ({ ...prev, [`${serviceId}_${field}`]: true }));
    searchAirports(value, serviceId, field);
  };

  const selectAirport = (serviceId, field, airport) => {
    const displayValue = `(${airport.iata_code}) ${airport.city}`;
    updateService(serviceId, field, displayValue);
    setAirportSearches(prev => ({ ...prev, [`${serviceId}_${field}`]: displayValue }));
    setShowAirportDropdown(prev => ({ ...prev, [`${serviceId}_${field}`]: false }));
    setAirportResults(prev => ({ ...prev, [`${serviceId}_${field}`]: [] }));
  };

  const formatAirportOption = (airport) => {
    const countryDisplay = airport.country_name 
      ? `${airport.country} - ${airport.country_name}`
      : airport.country;
    return (
      <span>
        <strong>({airport.iata_code})</strong> - {airport.city} - {countryDisplay} - <em className="text-slate-500">{airport.name}</em>
      </span>
    );
  };

  // Destination search state
  const [destinationSearches, setDestinationSearches] = useState({});
  const [destinationResults, setDestinationResults] = useState({});
  const [showDestinationDropdown, setShowDestinationDropdown] = useState({});

  // Search destinations
  const searchDestinations = async (query, fieldKey) => {
    if (query.length < 2) {
      setDestinationResults(prev => ({ ...prev, [fieldKey]: [] }));
      return;
    }
    try {
      const res = await axios.get(`${API}/destinations/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDestinationResults(prev => ({ ...prev, [fieldKey]: res.data }));
    } catch (error) {
      console.error("Error searching destinations:", error);
    }
  };

  const handleDestinationSearchChange = (fieldKey, value, updateFn) => {
    setDestinationSearches(prev => ({ ...prev, [fieldKey]: value }));
    setShowDestinationDropdown(prev => ({ ...prev, [fieldKey]: true }));
    searchDestinations(value, fieldKey);
  };

  const selectDestination = (fieldKey, destination, updateFn) => {
    const displayValue = `${destination.city}, ${destination.state}, ${destination.country}`;
    updateFn(displayValue);
    setDestinationSearches(prev => ({ ...prev, [fieldKey]: displayValue }));
    setShowDestinationDropdown(prev => ({ ...prev, [fieldKey]: false }));
    setDestinationResults(prev => ({ ...prev, [fieldKey]: [] }));
  };

  const formatDestinationOption = (dest) => {
    return (
      <span>
        <strong>{dest.city}</strong>, {dest.state}, {dest.country}
      </span>
    );
  };

  // Airlines search state for flight services
  const [airlineSearches, setAirlineSearches] = useState({});
  const [airlineResults, setAirlineResults] = useState({});
  const [showAirlineDropdown, setShowAirlineDropdown] = useState({});

  // Search airlines
  const searchAirlines = async (query, serviceId) => {
    if (query.length < 2) {
      setAirlineResults(prev => ({ ...prev, [serviceId]: [] }));
      return;
    }
    try {
      const res = await axios.get(`${API}/airlines/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAirlineResults(prev => ({ ...prev, [serviceId]: res.data }));
    } catch (error) {
      console.error("Error searching airlines:", error);
    }
  };

  const handleAirlineSearchChange = (serviceId, value) => {
    setAirlineSearches(prev => ({ ...prev, [serviceId]: value }));
    setShowAirlineDropdown(prev => ({ ...prev, [serviceId]: true }));
    searchAirlines(value, serviceId);
    updateService(serviceId, "provider", value);
  };

  const selectAirline = (serviceId, airline) => {
    const displayValue = `${airline.name} (${airline.code})`;
    updateService(serviceId, "provider", displayValue);
    setAirlineSearches(prev => ({ ...prev, [serviceId]: displayValue }));
    setShowAirlineDropdown(prev => ({ ...prev, [serviceId]: false }));
    setAirlineResults(prev => ({ ...prev, [serviceId]: [] }));
  };

  const formatAirlineOption = (airline) => {
    return (
      <span>
        <strong>{airline.name}</strong> ({airline.code}) - {airline.country}
        {airline.is_domestic_mexico && <span className="ml-2 text-xs bg-green-100 text-green-700 px-1 rounded">MX</span>}
      </span>
    );
  };

  // Hotels search state for hotel services
  const [hotelSearches, setHotelSearches] = useState({});
  const [hotelResults, setHotelResults] = useState({});
  const [showHotelDropdown, setShowHotelDropdown] = useState({});

  // Search hotels
  const searchHotels = async (query, serviceId) => {
    if (query.length < 2) {
      setHotelResults(prev => ({ ...prev, [serviceId]: [] }));
      return;
    }
    try {
      const res = await axios.get(`${API}/hotels/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHotelResults(prev => ({ ...prev, [serviceId]: res.data }));
    } catch (error) {
      console.error("Error searching hotels:", error);
    }
  };

  const handleHotelSearchChange = (serviceId, value) => {
    setHotelSearches(prev => ({ ...prev, [serviceId]: value }));
    setShowHotelDropdown(prev => ({ ...prev, [serviceId]: true }));
    searchHotels(value, serviceId);
    updateService(serviceId, "provider", value);
  };

  const selectHotel = (serviceId, hotel) => {
    const displayValue = `${hotel.hotel_name} (${hotel.chain_name})`;
    updateService(serviceId, "provider", displayValue);
    setHotelSearches(prev => ({ ...prev, [serviceId]: displayValue }));
    setShowHotelDropdown(prev => ({ ...prev, [serviceId]: false }));
    setHotelResults(prev => ({ ...prev, [serviceId]: [] }));
  };

  const formatHotelOption = (hotel) => {
    return (
      <span>
        <strong>{hotel.hotel_name}</strong> - {hotel.chain_name} - {hotel.city}, {hotel.country}
      </span>
    );
  };

  const initialFormData = {
    project_name: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    client_company: "",
    client_id: "",
    client_logo_url: "",
    business_unit: "",
    quote_type: "FIT",
    category: "LEISURE",
    destination: "",
    origin: "",
    start_date: null,
    end_date: null,
    passengers: { adults: 1, children: 0, infants: 0 },
    services: [],
    notes: "",
    terms: "",
    validity_days: 15,
    currency: "USD",
    status: "draft",
    assigned_user_id: "",
    assigned_user_name: ""
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchSettings();
    fetchUsers();
  }, []);

  // Set current user as default when users load
  useEffect(() => {
    if (user && !formData.assigned_user_id) {
      setFormData(prev => ({
        ...prev,
        assigned_user_id: user.id || user.email,
        assigned_user_name: user.name || user.email
      }));
    }
  }, [user]);

  // Detectar cambios en el formulario
  useEffect(() => {
    const hasChanges = formData.project_name !== "" ||
      formData.client_name !== "" ||
      formData.client_company !== "" ||
      formData.destination !== "" ||
      formData.services.length > 0;
    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
      setFormData(prev => ({
        ...prev,
        terms: res.data.default_terms || prev.terms,
        currency: res.data.default_currency || prev.currency
      }));
      // Update editor content with default terms
      if (termsEditor && res.data.default_terms) {
        termsEditor.commands.setContent(res.data.default_terms);
      }
    } catch (error) {
      console.error("Error fetching settings", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter users based on role
      let users = res.data.filter(u => u.is_active);
      
      if (user?.role === "ejecutivo") {
        // Ejecutivo only sees themselves
        users = users.filter(u => u.id === user.id || u.email === user.email);
      }
      // Admin and Supervisor see all users
      
      setAvailableUsers(users);
    } catch (error) {
      console.error("Error fetching users", error);
      // If can't fetch users, at least show current user
      if (user) {
        setAvailableUsers([{ id: user.id || user.email, name: user.name, email: user.email }]);
      }
    }
  };

  // Client search function
  const searchClients = async (query) => {
    if (!query || query.length < 2) {
      setClientResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/clients/search`, {
        params: { q: query },
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientResults(res.data);
    } catch (error) {
      console.error("Error searching clients:", error);
    }
  };

  // Handle client search input change
  const handleClientSearchChange = (value) => {
    setClientSearch(value);
    setShowClientDropdown(true);
    searchClients(value);
    
    // If user clears or changes the search, clear selected client
    if (selectedClient && value !== selectedClient.company_name) {
      setSelectedClient(null);
      setFormData(prev => ({
        ...prev,
        client_id: "",
        client_company: value,
        client_name: "",
        client_email: "",
        client_phone: ""
      }));
    } else {
      updateField("client_company", value);
    }
  };

  // Handle client selection from dropdown
  const selectClient = (client) => {
    setSelectedClient(client);
    setClientSearch(client.company_name);
    setShowClientDropdown(false);
    setFormData(prev => ({
      ...prev,
      client_id: client.id,
      client_company: client.nombre_comercial || client.company_name,
      client_name: client.contact_name,
      client_email: client.email,
      client_phone: client.phone,
      client_logo_url: client.logo_url || ""
    }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updatePassengers = (field, value) => {
    // Permitir valores vacíos temporalmente mientras el usuario escribe
    const numValue = value === "" ? "" : parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      passengers: { ...prev.passengers, [field]: numValue }
    }));
  };

  // Helper para campos numéricos - permite vacío temporalmente
  const parseNumber = (value, defaultValue = 0) => {
    if (value === "" || value === null || value === undefined) return "";
    const num = parseFloat(value);
    return isNaN(num) ? defaultValue : num;
  };

  const parseInteger = (value, defaultValue = 1) => {
    if (value === "" || value === null || value === undefined) return "";
    const num = parseInt(value);
    return isNaN(num) ? defaultValue : num;
  };

  const createEmptyService = (type = "flight") => ({
    id: `svc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type,
    description: "",
    provider: "",
    unit_cost: 0,
    margin_percentage: 10,
    unit_price: 0,
    quantity: 1,
    service_iva_percentage: 16, // IVA por servicio (0 o 16)
    service_iva_amount: 0,
    price_with_iva: 0,
    // Flight fields
    flight_type: "roundtrip",
    flight_origin: "",
    flight_destination: "",
    flight_number: "",
    flight_start_date: "",
    flight_end_date: "",
    flight_pax: 1,
    flight_price_per_pax: 0,
    // Hotel fields
    hotel_destination: "",
    hotel_name: "",
    hotel_start_date: "",
    hotel_end_date: "",
    hotel_room_count: 1,
    hotel_room_type: "doble",
    hotel_nights: 0,
    hotel_cost_per_night: 0,
    hotel_price_per_night: 0,
    // Transfer fields
    transfer_origin: "",
    transfer_destination: "",
    transfer_start_date: "",
    transfer_end_date: "",
    transfer_vehicle_type: "",
    transfer_persons_per_vehicle: 1,
    // Assistance fields (Asistencia al Viajero)
    assistance_coverage_type: "estandar",
    assistance_destination: "",
    assistance_start_date: "",
    assistance_end_date: "",
    // Tour fields
    tour_destination: "",
    tour_name: "",
    // Other fields
    other_destination: "",
    notes: ""
  });

  const addService = () => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, createEmptyService()]
    }));
  };

  const updateService = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map(s => {
        if (s.id !== id) return s;
        
        const updated = { ...s, [field]: value };
        
        // Clear end date if switching to oneway
        if (field === "flight_type" && value === "oneway") {
          updated.flight_end_date = "";
        }
        
        // Calculate nights for hotel
        if (field === "hotel_start_date" || field === "hotel_end_date") {
          if (updated.hotel_start_date && updated.hotel_end_date) {
            const start = parseISO(updated.hotel_start_date);
            const end = parseISO(updated.hotel_end_date);
            updated.hotel_nights = Math.max(0, differenceInDays(end, start));
          }
        }
        
        // Helper to get numeric value (handles empty strings)
        const getNum = (val, def = 0) => (val === "" || val === null || val === undefined) ? def : (parseFloat(val) || def);
        
        // Calculate prices based on type
        let totalCost = 0;
        let unitPrice = 0;
        
        if (updated.type === "hotel") {
          totalCost = getNum(updated.hotel_nights) * getNum(updated.hotel_room_count, 1) * getNum(updated.hotel_cost_per_night);
          const totalPrice = totalCost * (1 + getNum(updated.margin_percentage) / 100);
          if (getNum(updated.hotel_nights) > 0 && getNum(updated.hotel_room_count, 1) > 0) {
            updated.hotel_price_per_night = totalPrice / (getNum(updated.hotel_nights) * getNum(updated.hotel_room_count, 1));
          }
          unitPrice = totalPrice;
        } else if (updated.type === "flight") {
          // Costo por PAX
          const costPerPax = getNum(updated.unit_cost);
          const pax = getNum(updated.flight_pax, 1);
          const margin = getNum(updated.margin_percentage);
          
          // Precio por PAX (con margen)
          const pricePerPax = costPerPax * (1 + margin / 100);
          updated.flight_price_per_pax = pricePerPax;
          
          // Costo y precio total
          totalCost = costPerPax * pax;
          unitPrice = pricePerPax * pax;
        } else {
          totalCost = getNum(updated.unit_cost) * getNum(updated.quantity, 1);
          unitPrice = totalCost * (1 + getNum(updated.margin_percentage) / 100);
        }
        
        // Calculate per-service IVA
        const serviceIva = updated.service_iva_percentage || 0;
        const serviceIvaAmount = unitPrice * (serviceIva / 100);
        
        updated.calculated_cost = totalCost;
        updated.unit_price = unitPrice;
        updated.service_iva_amount = serviceIvaAmount;
        updated.price_with_iva = unitPrice + serviceIvaAmount;
        
        return updated;
      })
    }));
  };

  const removeService = (id) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(s => s.id !== id)
    }));
  };

  const moveService = (index, direction) => {
    setFormData(prev => {
      const newServices = [...prev.services];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      
      // Verificar límites
      if (newIndex < 0 || newIndex >= newServices.length) return prev;
      
      // Intercambiar posiciones
      [newServices[index], newServices[newIndex]] = [newServices[newIndex], newServices[index]];
      
      return { ...prev, services: newServices };
    });
  };

  const calculateTotals = () => {
    let totalCost = 0;
    let subtotal = 0;
    let totalIva = 0;
    
    formData.services.forEach(service => {
      totalCost += service.calculated_cost || 0;
      subtotal += service.unit_price || 0;
      totalIva += service.service_iva_amount || 0;
    });
    
    const total = subtotal + totalIva;
    
    return {
      totalCost,
      subtotal,
      ivaAmount: totalIva,
      total,
      profit: subtotal - totalCost
    };
  };

  // Calculate summary by service type
  const getServiceSummaryByType = () => {
    if (!formData.services?.length) return [];
    
    const summaryMap = {};
    
    formData.services.forEach(service => {
      const type = service.type || 'other';
      if (!summaryMap[type]) {
        const serviceType = serviceTypes.find(t => t.value === type);
        summaryMap[type] = {
          type,
          label: serviceType?.label || type,
          icon: serviceType?.icon || Plus,
          subtotal: 0,
          iva: 0,
          total: 0
        };
      }
      
      const unitPrice = service.unit_price || 0;
      const ivaAmount = service.service_iva_amount || 0;
      const priceWithIva = unitPrice + ivaAmount;
      
      summaryMap[type].subtotal += unitPrice;
      summaryMap[type].iva += ivaAmount;
      summaryMap[type].total += priceWithIva;
    });
    
    // Convert to array and maintain order of appearance
    const typeOrder = [];
    formData.services.forEach(service => {
      const type = service.type || 'other';
      if (!typeOrder.includes(type)) {
        typeOrder.push(type);
      }
    });
    
    return typeOrder.map(type => summaryMap[type]);
  };

  const handleSave = async (status = "draft") => {
    if (!formData.client_name || !formData.destination) {
      toast.error("Por favor completa el nombre del cliente y destino");
      return;
    }

    setLoading(true);
    try {
      const totals = calculateTotals();
      const payload = {
        ...formData,
        start_date: formData.start_date ? format(formData.start_date, "yyyy-MM-dd") : "",
        end_date: formData.end_date ? format(formData.end_date, "yyyy-MM-dd") : "",
        subtotal: totals.subtotal,
        iva_amount: totals.ivaAmount,
        total: totals.total,
        status
      };

      const res = await axios.post(`${API}/quotes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setHasUnsavedChanges(false);
      toast.success(status === "draft" ? "Borrador guardado" : "Cotización guardada");
      const quoteId = res.data?.id;
      if (quoteId) {
        navigate(`/cotizacion/${quoteId}`);
      } else {
        navigate("/historial");
      }
    } catch (error) {
      console.error("Error saving quote:", error);
      toast.error("Error al guardar cotización");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardChanges = () => {
    setFormData({
      ...initialFormData,
      assigned_user_id: user?.id || user?.email || "",
      assigned_user_name: user?.name || user?.email || "",
      terms: settings?.default_terms || ""
    });
    setClientSearch("");
    setSelectedClient(null);
    setHasUnsavedChanges(false);
  };

  const totals = calculateTotals();

  const renderServiceFields = (service, index) => {
    const ServiceIcon = serviceTypes.find(t => t.value === service.type)?.icon || Plus;
    
    return (
      <div
        key={service.id}
        className="service-item animate-fade-in"
        data-testid={`service-item-${index}`}
      >
        <div className="flex items-start gap-4">
          {/* Controles de orden */}
          <div className="flex flex-col items-center gap-1 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => moveService(index, 'up')}
              disabled={index === 0}
              title="Mover arriba"
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
            <GripVertical className="w-4 h-4 text-slate-300" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => moveService(index, 'down')}
              disabled={index === formData.services.length - 1}
              title="Mover abajo"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <ServiceIcon className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1 space-y-4">
            {/* Service Type & Description */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Servicio</Label>
                <Select
                  value={service.type}
                  onValueChange={(v) => updateService(service.id, "type", v)}
                >
                  <SelectTrigger data-testid={`service-type-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Descripción</Label>
                <Input
                  value={service.description}
                  onChange={(e) => updateService(service.id, "description", e.target.value)}
                  placeholder="Descripción del servicio"
                  data-testid={`service-description-${index}`}
                />
              </div>
            </div>

            {/* FLIGHT Fields */}
            {service.type === "flight" && (
              <>
                <div className="grid grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Vuelo</Label>
                    <Select
                      value={service.flight_type || "roundtrip"}
                      onValueChange={(v) => updateService(service.id, "flight_type", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {flightTypes.map(ft => (
                          <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 relative">
                    <Label>Origen</Label>
                    <Input
                      value={airportSearches[`${service.id}_flight_origin`] ?? service.flight_origin}
                      onChange={(e) => handleAirportSearchChange(service.id, "flight_origin", e.target.value)}
                      onFocus={() => setShowAirportDropdown(prev => ({ ...prev, [`${service.id}_flight_origin`]: true }))}
                      placeholder="Buscar aeropuerto..."
                    />
                    {showAirportDropdown[`${service.id}_flight_origin`] && airportResults[`${service.id}_flight_origin`]?.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {airportResults[`${service.id}_flight_origin`].map((airport) => (
                          <div
                            key={airport.iata_code}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectAirport(service.id, "flight_origin", airport)}
                          >
                            {formatAirportOption(airport)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 relative">
                    <Label>Destino</Label>
                    <Input
                      value={airportSearches[`${service.id}_flight_destination`] ?? service.flight_destination}
                      onChange={(e) => handleAirportSearchChange(service.id, "flight_destination", e.target.value)}
                      onFocus={() => setShowAirportDropdown(prev => ({ ...prev, [`${service.id}_flight_destination`]: true }))}
                      placeholder="Buscar aeropuerto..."
                    />
                    {showAirportDropdown[`${service.id}_flight_destination`] && airportResults[`${service.id}_flight_destination`]?.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {airportResults[`${service.id}_flight_destination`].map((airport) => (
                          <div
                            key={airport.iata_code}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectAirport(service.id, "flight_destination", airport)}
                          >
                            {formatAirportOption(airport)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha Salida</Label>
                    <Input
                      type="date"
                      value={service.flight_start_date}
                      onChange={(e) => updateService(service.id, "flight_start_date", e.target.value)}
                    />
                  </div>
                  {service.flight_type !== "oneway" && (
                    <div className="space-y-2">
                      <Label>Fecha Regreso</Label>
                      <Input
                        type="date"
                        value={service.flight_end_date}
                        onChange={(e) => updateService(service.id, "flight_end_date", e.target.value)}
                      />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label>No. Vuelo (opcional)</Label>
                    <Input
                      value={service.flight_number}
                      onChange={(e) => updateService(service.id, "flight_number", e.target.value)}
                      placeholder="AM123"
                    />
                  </div>
                  <div className="space-y-2 relative">
                    <Label>Proveedor (Aerolínea)</Label>
                    <Input
                      value={airlineSearches[service.id] ?? service.provider}
                      onChange={(e) => handleAirlineSearchChange(service.id, e.target.value)}
                      onFocus={() => setShowAirlineDropdown(prev => ({ ...prev, [service.id]: true }))}
                      placeholder="Buscar aerolínea..."
                    />
                    {showAirlineDropdown[service.id] && airlineResults[service.id]?.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {airlineResults[service.id].map((airline) => (
                          <div
                            key={airline.id}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectAirline(service.id, airline)}
                          >
                            {formatAirlineOption(airline)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Pasajeros (PAX)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={service.flight_pax}
                      onChange={(e) => updateService(service.id, "flight_pax", parseInteger(e.target.value, 1))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Costo por PAX</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={service.unit_cost}
                      onChange={(e) => updateService(service.id, "unit_cost", parseNumber(e.target.value, 0))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio por PAX</Label>
                    <Input
                      type="text"
                      value={`${formData.currency} ${(service.flight_price_per_pax || 0).toFixed(2)}`}
                      disabled
                      className="bg-amber-50 font-semibold text-amber-700"
                    />
                  </div>
                </div>
              </>
            )}

            {/* HOTEL Fields */}
            {service.type === "hotel" && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2 relative">
                    <Label>Destino</Label>
                    <Input
                      value={destinationSearches[`${service.id}_hotel_destination`] ?? service.hotel_destination}
                      onChange={(e) => {
                        handleDestinationSearchChange(`${service.id}_hotel_destination`, e.target.value, (v) => updateService(service.id, "hotel_destination", v));
                        updateService(service.id, "hotel_destination", e.target.value);
                      }}
                      onFocus={() => setShowDestinationDropdown(prev => ({ ...prev, [`${service.id}_hotel_destination`]: true }))}
                      placeholder="Buscar destino..."
                    />
                    {showDestinationDropdown[`${service.id}_hotel_destination`] && destinationResults[`${service.id}_hotel_destination`]?.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {destinationResults[`${service.id}_hotel_destination`].map((dest) => (
                          <div
                            key={dest.id}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectDestination(`${service.id}_hotel_destination`, dest, (v) => updateService(service.id, "hotel_destination", v))}
                          >
                            {formatDestinationOption(dest)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 relative">
                    <Label>Hotel</Label>
                    <Input
                      value={hotelSearches[service.id] ?? service.provider}
                      onChange={(e) => handleHotelSearchChange(service.id, e.target.value)}
                      onFocus={() => setShowHotelDropdown(prev => ({ ...prev, [service.id]: true }))}
                      placeholder="Buscar hotel..."
                    />
                    {showHotelDropdown[service.id] && hotelResults[service.id]?.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {hotelResults[service.id].map((hotel) => (
                          <div
                            key={hotel.id}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectHotel(service.id, hotel)}
                          >
                            {formatHotelOption(hotel)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Habitación</Label>
                    <Select
                      value={service.hotel_room_type}
                      onValueChange={(v) => updateService(service.id, "hotel_room_type", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {roomTypes.map(rt => (
                          <SelectItem key={rt.value} value={rt.value}>
                            {rt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Check-in</Label>
                    <Input
                      type="date"
                      value={service.hotel_start_date}
                      onChange={(e) => updateService(service.id, "hotel_start_date", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Check-out</Label>
                    <Input
                      type="date"
                      value={service.hotel_end_date}
                      onChange={(e) => updateService(service.id, "hotel_end_date", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Noches</Label>
                    <Input
                      type="number"
                      value={service.hotel_nights}
                      disabled
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cant. Habitaciones</Label>
                    <Input
                      type="number"
                      min="1"
                      value={service.hotel_room_count}
                      onChange={(e) => updateService(service.id, "hotel_room_count", parseInteger(e.target.value, 1))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Costo por Noche</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={service.hotel_cost_per_night}
                      onChange={(e) => updateService(service.id, "hotel_cost_per_night", parseNumber(e.target.value, 0))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio por Noche</Label>
                    <Input
                      type="text"
                      value={`${formData.currency} ${(service.hotel_price_per_night || 0).toFixed(2)}`}
                      disabled
                      className="bg-amber-50 font-semibold text-amber-700"
                    />
                  </div>
                </div>
              </>
            )}

            {/* TRANSFER Fields */}
            {service.type === "transfer" && (
              <>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Origen</Label>
                    <Input
                      value={service.transfer_origin}
                      onChange={(e) => updateService(service.id, "transfer_origin", e.target.value)}
                      placeholder="Aeropuerto CUN"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Destino</Label>
                    <Input
                      value={service.transfer_destination}
                      onChange={(e) => updateService(service.id, "transfer_destination", e.target.value)}
                      placeholder="Hotel Zona Hotelera"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha Inicio</Label>
                    <Input
                      type="date"
                      value={service.transfer_start_date}
                      onChange={(e) => updateService(service.id, "transfer_start_date", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha Fin</Label>
                    <Input
                      type="date"
                      value={service.transfer_end_date}
                      onChange={(e) => updateService(service.id, "transfer_end_date", e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Vehículo</Label>
                    <Input
                      value={service.transfer_vehicle_type}
                      onChange={(e) => updateService(service.id, "transfer_vehicle_type", e.target.value)}
                      placeholder="Suburban, Van, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Personas por Vehículo</Label>
                    <Input
                      type="number"
                      min="1"
                      value={service.transfer_persons_per_vehicle}
                      onChange={(e) => updateService(service.id, "transfer_persons_per_vehicle", parseInteger(e.target.value, 1))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cantidad</Label>
                    <Input
                      type="number"
                      min="1"
                      value={service.quantity}
                      onChange={(e) => updateService(service.id, "quantity", parseInteger(e.target.value, 1))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Costo</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={service.unit_cost}
                      onChange={(e) => updateService(service.id, "unit_cost", parseNumber(e.target.value, 0))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio Unitario</Label>
                    <Input
                      type="text"
                      value={`${formData.currency} ${((service.unit_price || 0) / (service.quantity || 1)).toFixed(2)}`}
                      disabled
                      className="bg-amber-50 font-semibold text-amber-700"
                    />
                  </div>
                </div>
              </>
            )}

            {/* ASSISTANCE Fields (Asistencia al Viajero) */}
            {service.type === "assistance" && (
              <>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Cobertura</Label>
                    <Select 
                      value={service.assistance_coverage_type || "estandar"} 
                      onValueChange={(v) => updateService(service.id, "assistance_coverage_type", v)}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {coverageTypes.map(ct => (
                          <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 relative">
                    <Label>Destino</Label>
                    <Input
                      value={destinationSearches[`${service.id}_assistance_destination`] ?? service.assistance_destination}
                      onChange={(e) => {
                        handleDestinationSearchChange(`${service.id}_assistance_destination`, e.target.value, (v) => updateService(service.id, "assistance_destination", v));
                        updateService(service.id, "assistance_destination", e.target.value);
                      }}
                      onFocus={() => setShowDestinationDropdown(prev => ({ ...prev, [`${service.id}_assistance_destination`]: true }))}
                      placeholder="Buscar destino..."
                    />
                    {showDestinationDropdown[`${service.id}_assistance_destination`] && destinationResults[`${service.id}_assistance_destination`]?.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {destinationResults[`${service.id}_assistance_destination`].map((dest) => (
                          <div
                            key={dest.id}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectDestination(`${service.id}_assistance_destination`, dest, (v) => updateService(service.id, "assistance_destination", v))}
                          >
                            {formatDestinationOption(dest)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha Inicio</Label>
                    <Input
                      type="date"
                      value={service.assistance_start_date}
                      onChange={(e) => updateService(service.id, "assistance_start_date", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha Fin</Label>
                    <Input
                      type="date"
                      value={service.assistance_end_date}
                      onChange={(e) => updateService(service.id, "assistance_end_date", e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label>Proveedor</Label>
                    <Input
                      value={service.provider}
                      onChange={(e) => updateService(service.id, "provider", e.target.value)}
                      placeholder="Assist Card, World Nomads, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cantidad</Label>
                    <Input
                      type="number"
                      min="1"
                      value={service.quantity}
                      onChange={(e) => updateService(service.id, "quantity", parseInteger(e.target.value, 1))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Costo</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={service.unit_cost}
                      onChange={(e) => updateService(service.id, "unit_cost", parseNumber(e.target.value, 0))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio Unitario</Label>
                    <Input
                      type="text"
                      value={`${formData.currency} ${((service.unit_price || 0) / (service.quantity || 1)).toFixed(2)}`}
                      disabled
                      className="bg-amber-50 font-semibold text-amber-700"
                    />
                  </div>
                </div>
              </>
            )}

            {/* TOUR Fields */}
            {service.type === "tour" && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2 relative">
                    <Label>Destino</Label>
                    <Input
                      value={destinationSearches[`${service.id}_tour_destination`] ?? service.tour_destination}
                      onChange={(e) => {
                        handleDestinationSearchChange(`${service.id}_tour_destination`, e.target.value, (v) => updateService(service.id, "tour_destination", v));
                        updateService(service.id, "tour_destination", e.target.value);
                      }}
                      onFocus={() => setShowDestinationDropdown(prev => ({ ...prev, [`${service.id}_tour_destination`]: true }))}
                      placeholder="Buscar destino..."
                    />
                    {showDestinationDropdown[`${service.id}_tour_destination`] && destinationResults[`${service.id}_tour_destination`]?.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {destinationResults[`${service.id}_tour_destination`].map((dest) => (
                          <div
                            key={dest.id}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectDestination(`${service.id}_tour_destination`, dest, (v) => updateService(service.id, "tour_destination", v))}
                          >
                            {formatDestinationOption(dest)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre del Tour/Actividad</Label>
                    <Input
                      value={service.tour_name}
                      onChange={(e) => updateService(service.id, "tour_name", e.target.value)}
                      placeholder="Tour a Chichén Itzá"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Proveedor</Label>
                    <Input
                      value={service.provider}
                      onChange={(e) => updateService(service.id, "provider", e.target.value)}
                      placeholder="Proveedor"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Cantidad</Label>
                    <Input
                      type="number"
                      min="1"
                      value={service.quantity}
                      onChange={(e) => updateService(service.id, "quantity", parseInteger(e.target.value, 1))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Costo</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={service.unit_cost}
                      onChange={(e) => updateService(service.id, "unit_cost", parseNumber(e.target.value, 0))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio Unitario</Label>
                    <Input
                      type="text"
                      value={`${formData.currency} ${((service.unit_price || 0) / (service.quantity || 1)).toFixed(2)}`}
                      disabled
                      className="bg-amber-50 font-semibold text-amber-700"
                    />
                  </div>
                </div>
              </>
            )}

            {/* OTHER Fields */}
            {service.type === "other" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 relative">
                    <Label>Destino</Label>
                    <Input
                      value={destinationSearches[`${service.id}_other_destination`] ?? service.other_destination}
                      onChange={(e) => {
                        handleDestinationSearchChange(`${service.id}_other_destination`, e.target.value, (v) => updateService(service.id, "other_destination", v));
                        updateService(service.id, "other_destination", e.target.value);
                      }}
                      onFocus={() => setShowDestinationDropdown(prev => ({ ...prev, [`${service.id}_other_destination`]: true }))}
                      placeholder="Buscar destino..."
                    />
                    {showDestinationDropdown[`${service.id}_other_destination`] && destinationResults[`${service.id}_other_destination`]?.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {destinationResults[`${service.id}_other_destination`].map((dest) => (
                          <div
                            key={dest.id}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectDestination(`${service.id}_other_destination`, dest, (v) => updateService(service.id, "other_destination", v))}
                          >
                            {formatDestinationOption(dest)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Proveedor</Label>
                    <Input
                      value={service.provider}
                      onChange={(e) => updateService(service.id, "provider", e.target.value)}
                      placeholder="Proveedor"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Cantidad</Label>
                    <Input
                      type="number"
                      min="1"
                      value={service.quantity}
                      onChange={(e) => updateService(service.id, "quantity", parseInteger(e.target.value, 1))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Costo</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={service.unit_cost}
                      onChange={(e) => updateService(service.id, "unit_cost", parseNumber(e.target.value, 0))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio Unitario</Label>
                    <Input
                      type="text"
                      value={`${formData.currency} ${((service.unit_price || 0) / (service.quantity || 1)).toFixed(2)}`}
                      disabled
                      className="bg-amber-50 font-semibold text-amber-700"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Generic fields for other types (meal, conference) */}
            {!["flight", "hotel", "transfer", "assistance", "tour", "other"].includes(service.type) && (
              <div className="grid grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label>Proveedor</Label>
                  <Input
                    value={service.provider}
                    onChange={(e) => updateService(service.id, "provider", e.target.value)}
                    placeholder="Proveedor"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cantidad</Label>
                  <Input
                    type="number"
                    min="1"
                    value={service.quantity}
                    onChange={(e) => updateService(service.id, "quantity", parseInteger(e.target.value, 1))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Costo</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={service.unit_cost}
                    onChange={(e) => updateService(service.id, "unit_cost", parseNumber(e.target.value, 0))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Precio Unitario</Label>
                  <Input
                    type="text"
                    value={`${formData.currency} ${((service.unit_price || 0) / (service.quantity || 1)).toFixed(2)}`}
                    disabled
                    className="bg-amber-50 font-semibold text-amber-700"
                  />
                </div>
              </div>
            )}

            {/* Margin, IVA and Notes - common to all */}
            <div className="grid grid-cols-5 gap-4 pt-2 border-t">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Percent className="w-3 h-3" />
                  Margen (%)
                </Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={service.margin_percentage}
                  onChange={(e) => updateService(service.id, "margin_percentage", parseNumber(e.target.value, 0))}
                  data-testid={`service-margin-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>IVA Aplicable</Label>
                <Select 
                  value={String(service.service_iva_percentage ?? 16)} 
                  onValueChange={(v) => updateService(service.id, "service_iva_percentage", parseFloat(v))}
                >
                  <SelectTrigger data-testid={`service-iva-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ivaOptions.map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Precio (sin IVA)</Label>
                <Input
                  type="text"
                  value={`${formData.currency} ${(service.unit_price || 0).toFixed(2)}`}
                  disabled
                  className="bg-slate-100 font-semibold"
                />
              </div>
              <div className="space-y-2">
                <Label>Total c/IVA</Label>
                <Input
                  type="text"
                  value={`${formData.currency} ${(service.price_with_iva || service.unit_price || 0).toFixed(2)}`}
                  disabled
                  className="bg-slate-900 font-semibold text-amber-400"
                />
              </div>
              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Input
                  value={service.notes}
                  onChange={(e) => updateService(service.id, "notes", e.target.value)}
                  placeholder="Notas adicionales"
                />
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-red-500"
            onClick={() => removeService(service.id)}
            data-testid={`remove-service-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="new-quote-page">
      <UnsavedChangesGuard 
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={() => handleSave("draft")}
        onDiscard={handleDiscardChanges}
      />
      <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">
              Nueva Cotización
            </h1>
            <p className="text-slate-500">Completa los datos para generar la cotización</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            data-testid="toggle-preview-button"
          >
            <Eye className="w-4 h-4 mr-2" />
            {showPreview ? "Ocultar Vista Previa" : "Ver Vista Previa"}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave("draft")}
            disabled={loading}
            data-testid="save-draft-button"
          >
            <Save className="w-4 h-4 mr-2" />
            Guardar Borrador
          </Button>
          <Button
            className="action-button"
            onClick={() => handleSave("sent")}
            disabled={loading}
            data-testid="save-sent-button"
          >
            Guardar y Enviar
          </Button>
        </div>
      </div>

      <div className={`grid ${showPreview ? 'grid-cols-2' : 'grid-cols-1'} gap-6`}>
        {/* Form Column */}
        <div className="space-y-6">
          {/* Client Info */}
          <Card className="form-section border-0 shadow-sm" data-testid="client-section">
            <CardHeader className="pb-4">
              <CardTitle className="form-section-title">
                <User className="w-5 h-5 text-slate-600" />
                Información del Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Empresa / Razón Social *</Label>
                  <ClientCombobox
                    value={formData.client_company}
                    onSelect={selectClient}
                    onClear={() => { setSelectedClient(null); setFormData(prev => ({ ...prev, client_id: "", client_company: "" })); }}
                    testid="client-company-input"
                  />
                  {selectedClient && (
                    <p className="text-xs text-emerald-600">Cliente seleccionado: {selectedClient.folio}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Área / Unidad de Negocio</Label>
                  <Select value={formData.business_unit} onValueChange={(v) => updateField("business_unit", v)}>
                    <SelectTrigger data-testid="business-unit-select">
                      <SelectValue placeholder="Seleccionar unidad..." />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.business_units?.map((unit, idx) => (
                        <SelectItem key={idx} value={unit}>{unit}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client_name">Nombre del Contacto *</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => updateField("client_name", e.target.value)}
                    placeholder="Nombre de la persona de contacto"
                    data-testid="client-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project_name">Nombre del Proyecto</Label>
                  <Input
                    id="project_name"
                    value={formData.project_name}
                    onChange={(e) => updateField("project_name", e.target.value)}
                    placeholder="Ej: Viaje Corporativo Acme 2026"
                    data-testid="project-name-input"
                    className="font-semibold"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client_email">Email</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => updateField("client_email", e.target.value)}
                    placeholder="email@ejemplo.com"
                    data-testid="client-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_phone">Teléfono</Label>
                  <Input
                    id="client_phone"
                    value={formData.client_phone}
                    onChange={(e) => updateField("client_phone", e.target.value)}
                    placeholder="+52 55 1234 5678"
                    data-testid="client-phone-input"
                  />
                </div>
              </div>
              {/* Assigned User */}
              <div className="space-y-2">
                <Label>Cotización realizada por *</Label>
                <Select
                  value={formData.assigned_user_id}
                  onValueChange={(v) => {
                    const selectedUser = availableUsers.find(u => u.id === v);
                    updateField("assigned_user_id", v);
                    updateField("assigned_user_name", selectedUser?.name || "");
                  }}
                >
                  <SelectTrigger data-testid="assigned-user-select">
                    <SelectValue placeholder="Selecciona usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Trip Details */}
          <Card className="form-section border-0 shadow-sm" data-testid="trip-section">
            <CardHeader className="pb-4">
              <CardTitle className="form-section-title">
                <MapPin className="w-5 h-5 text-slate-600" />
                Detalles del Viaje
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Viaje</Label>
                  <Select
                    value={formData.quote_type}
                    onValueChange={(v) => updateField("quote_type", v)}
                  >
                    <SelectTrigger data-testid="quote-type-select">
                      <SelectValue placeholder="Seleccionar tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.trip_types?.map((type, idx) => (
                        <SelectItem key={idx} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(v) => updateField("category", v)}
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Seleccionar categoría..." />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.categories?.map((cat, idx) => (
                        <SelectItem key={idx} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 relative">
                  <Label htmlFor="origin">Origen</Label>
                  <Input
                    id="origin"
                    value={destinationSearches["form_origin"] ?? formData.origin}
                    onChange={(e) => {
                      handleDestinationSearchChange("form_origin", e.target.value, (v) => updateField("origin", v));
                      updateField("origin", e.target.value);
                    }}
                    onFocus={() => setShowDestinationDropdown(prev => ({ ...prev, form_origin: true }))}
                    placeholder="Buscar destino..."
                    data-testid="origin-input"
                  />
                  {showDestinationDropdown["form_origin"] && destinationResults["form_origin"]?.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                      {destinationResults["form_origin"].map((dest) => (
                        <div
                          key={dest.id}
                          className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                          onClick={() => selectDestination("form_origin", dest, (v) => updateField("origin", v))}
                        >
                          {formatDestinationOption(dest)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2 relative">
                  <Label htmlFor="destination">Destino *</Label>
                  <Input
                    id="destination"
                    value={destinationSearches["form_destination"] ?? formData.destination}
                    onChange={(e) => {
                      handleDestinationSearchChange("form_destination", e.target.value, (v) => updateField("destination", v));
                      updateField("destination", e.target.value);
                    }}
                    onFocus={() => setShowDestinationDropdown(prev => ({ ...prev, form_destination: true }))}
                    placeholder="Buscar destino..."
                    data-testid="destination-input"
                  />
                  {showDestinationDropdown["form_destination"] && destinationResults["form_destination"]?.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                      {destinationResults["form_destination"].map((dest) => (
                        <div
                          key={dest.id}
                          className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                          onClick={() => selectDestination("form_destination", dest, (v) => updateField("destination", v))}
                        >
                          {formatDestinationOption(dest)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha de Inicio</Label>
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="start-date-button"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.start_date ? format(formData.start_date, "dd MMM yyyy", { locale: es }) : "Seleccionar fecha"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.start_date}
                        onSelect={(date) => {
                          updateField("start_date", date);
                          setStartDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Fecha de Fin</Label>
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="end-date-button"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.end_date ? format(formData.end_date, "dd MMM yyyy", { locale: es }) : "Seleccionar fecha"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.end_date}
                        onSelect={(date) => {
                          updateField("end_date", date);
                          setEndDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Passengers */}
          <Card className="form-section border-0 shadow-sm" data-testid="passengers-section">
            <CardHeader className="pb-4">
              <CardTitle className="form-section-title">
                <Users className="w-5 h-5 text-slate-600" />
                Pasajeros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="adults">Adultos</Label>
                  <Input
                    id="adults"
                    type="number"
                    min="0"
                    value={formData.passengers.adults}
                    onChange={(e) => updatePassengers("adults", e.target.value)}
                    data-testid="adults-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="children">Niños (2-11)</Label>
                  <Input
                    id="children"
                    type="number"
                    min="0"
                    value={formData.passengers.children}
                    onChange={(e) => updatePassengers("children", e.target.value)}
                    data-testid="children-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="infants">Infantes (0-2)</Label>
                  <Input
                    id="infants"
                    type="number"
                    min="0"
                    value={formData.passengers.infants}
                    onChange={(e) => updatePassengers("infants", e.target.value)}
                    data-testid="infants-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Currency & Validity - Before Services */}
          <Card className="form-section border-0 shadow-sm" data-testid="currency-validity-section">
            <CardHeader className="pb-4">
              <CardTitle className="form-section-title">
                <FileText className="w-5 h-5 text-slate-600" />
                Moneda y Validez
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Fecha de Cotización</Label>
                  <Input
                    type="text"
                    value={format(new Date(), "dd/MM/yyyy", { locale: es })}
                    disabled
                    className="bg-slate-100 font-medium"
                    data-testid="quote-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Moneda</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(v) => updateField("currency", v)}
                  >
                    <SelectTrigger data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD - Dólar</SelectItem>
                      <SelectItem value="MXN">MXN - Peso Mexicano</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="validity">Validez (días)</Label>
                  <Input
                    id="validity"
                    type="number"
                    min="1"
                    value={formData.validity_days}
                    onChange={(e) => updateField("validity_days", parseInteger(e.target.value, 15))}
                    data-testid="validity-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services */}
          <Card className="form-section border-0 shadow-sm" data-testid="services-section">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="form-section-title">
                  <Plane className="w-5 h-5 text-slate-600" />
                  Servicios
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addService}
                  data-testid="add-service-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Servicio
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.services.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                  <Plane className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No hay servicios agregados</p>
                  <Button
                    variant="link"
                    onClick={addService}
                    className="mt-2"
                  >
                    Agregar primer servicio
                  </Button>
                </div>
              ) : (
                formData.services.map((service, index) => renderServiceFields(service, index))
              )}
            </CardContent>
          </Card>

          {/* Pricing Summary */}
          <Card className="form-section border-0 shadow-sm" data-testid="pricing-section">
            <CardHeader className="pb-4">
              <CardTitle className="form-section-title">
                Resumen de Precios
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p>El IVA se calcula por cada servicio individualmente. Selecciona 0% o 16% en cada servicio.</p>
              </div>

              {/* Service Summary by Type */}
              {formData.services?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Resumen por Tipo de Servicio</h4>
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-800 text-white">
                          <th className="text-left py-3 px-4 font-semibold">Servicio</th>
                          <th className="text-right py-3 px-4 font-semibold">Subtotal</th>
                          <th className="text-right py-3 px-4 font-semibold">IVA</th>
                          <th className="text-right py-3 px-4 font-semibold">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getServiceSummaryByType().map((summary, index) => {
                          const Icon = summary.icon;
                          return (
                            <tr key={summary.type} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="py-3 px-4 align-middle">
                                <div className="flex items-center gap-2">
                                  <Icon className="w-4 h-4 text-amber-500" />
                                  <span className="font-medium text-slate-700">{summary.label}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right text-slate-600 align-middle">{formData.currency} {summary.subtotal.toFixed(2)}</td>
                              <td className="py-3 px-4 text-right text-slate-600 align-middle">{formData.currency} {summary.iva.toFixed(2)}</td>
                              <td className="py-3 px-4 text-right font-semibold text-slate-800 align-middle">{formData.currency} {summary.total.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <Separator />

              {/* Internal summary */}
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Costo Total (interno)</p>
                  <p className="text-lg font-semibold text-slate-600">{formData.currency} {totals.totalCost.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-600">Utilidad (interno)</p>
                  <p className="text-lg font-semibold text-emerald-600">{formData.currency} {totals.profit.toFixed(2)}</p>
                </div>
              </div>

              <Separator />

              {/* Client facing summary */}
              <div className="p-4 bg-slate-900 rounded-xl space-y-3">
                <div className="flex justify-between text-slate-300">
                  <span>Subtotal</span>
                  <span className="font-semibold">{formData.currency} {totals.subtotal.toFixed(2)}</span>
                </div>
                {totals.ivaAmount > 0 && (
                  <div className="flex justify-between text-slate-300">
                    <span>IVA</span>
                    <span className="font-semibold">{formData.currency} {totals.ivaAmount.toFixed(2)}</span>
                  </div>
                )}
                <Separator className="bg-slate-700" />
                <div className="flex justify-between items-center">
                  <span className="text-white text-lg">Total</span>
                  <span className="text-2xl font-bold text-amber-400">{formData.currency} {totals.total.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="form-section border-0 shadow-sm" data-testid="notes-section">
            <CardHeader className="pb-4">
              <CardTitle className="form-section-title">
                Notas y Términos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="notes">Notas Internas</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Notas internas (no se muestran al cliente)"
                  rows={3}
                  data-testid="notes-textarea"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="terms">Términos y Condiciones de Emisión y Servicio</Label>
                <div className="bg-white rounded-md border overflow-hidden" data-testid="terms-editor">
                  <EditorToolbar editor={termsEditor} />
                  <EditorContent 
                    editor={termsEditor} 
                    className="prose prose-sm max-w-none p-4 min-h-[150px] focus:outline-none [&_.ProseMirror]:min-h-[130px] [&_.ProseMirror]:outline-none"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Usa la barra de herramientas para dar formato al texto.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview Column */}
        {showPreview && (
          <div className="sticky top-8">
            <QuotePreviewPanel
              quote={{
                ...formData,
                start_date: formData.start_date ? format(formData.start_date, "yyyy-MM-dd") : "",
                end_date: formData.end_date ? format(formData.end_date, "yyyy-MM-dd") : "",
                subtotal: totals.subtotal,
                iva_amount: totals.ivaAmount,
                total: totals.total,
                quote_number: "NUEVO"
              }}
              settings={settings}
              showInternalInfo={false}
            />
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default NewQuote;
